import React, { useEffect, useMemo, useState } from 'react';
import { Search, X, Plus, Check, ArrowRight, ArrowLeft, ChevronDown } from 'lucide-react';
import { useStore, Recipe } from '../../store/useStore';
import MealPreferences from './MealPreferences';
import CreateMealModal from './CreateMealModal';
import { doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import NewPlanAlert from '../ui/custom/NewPlanAlert';
import { getMultiFactorResolver } from 'firebase/auth';

interface TemplateBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: Recipe[];
  onUpdateTemplate: (recipes: Recipe[]) => void;
}

const isPureNumber = (val: any) => {
  return typeof val === 'string' && /^\d+$/.test(val.trim());
};

const TemplateBuilderModal: React.FC<TemplateBuilderModalProps> = ({
  isOpen,
  onClose,
  template,
  onUpdateTemplate
}) => {
  const recipes = useStore((state) => state.recipes);
  const [currentStep, setCurrentStep] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [mealPreferences, setMealPreferences] = useState(['breakfast', 'lunch', 'dinner']);
  const user = useStore((state) => state.user);
  const mealTemplate = useStore((state) => state.mealTemplate);
  const updateMealTemplate = useStore((state) => state.updateMealTemplate);
  const [mealsPerWeek, setMealsPerWeek] = useState(14); // Default to 2 meals per day
  const [mealPriorities, setMealPriorities] = useState({
    breakfast: true,
    lunch: true,
    dinner: true
  });
  const [showAlert, setShowAlert] = useState(false);
  const { generateWeeklyPlan, weeklyPlan } = useStore();
  const [filter, setFilter] = useState("Breakfast");
  const [allOpen, setAllOpen] = useState(false);


  // const filteredRecipes = useMemo(() => {
  //   return recipes.filter((recipe) =>
  //     recipe.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
  //     recipe.category.some((cat) => cat.toLowerCase().includes(filter.toLowerCase()))
  //   );
  // }, [recipes, searchTerm, filter]);

  const splitRecipes = (mealTemplate: any, matchesFilter?: (recipe: any) => {}) => {
    const templateIds = new Set(mealTemplate.map((t: any) => t.id));

    const filtered = recipes.reduce<[Recipe[], Recipe[]]>(
      ([inT, notT], recipe) => {
        if (matchesFilter) {
          if (!matchesFilter(recipe)) return [inT, notT]; // skip early if it doesn't match
        }
        return templateIds.has(recipe.id)
          ? [[...inT, recipe], notT]
          : [inT, [...notT, recipe]];
      },
      [[], []]
    );

    return filtered;
  }

  const filteredRecipes = useMemo(() => {
    const search = searchTerm.toLowerCase();
    const catFilter = filter.toLowerCase();

    // Helper to check if recipe matches search + category filter
    const matchesFilter = (recipe: Recipe) =>
      recipe.title.toLowerCase().includes(search) &&
      recipe.category.some((c) => c.toLowerCase().includes(catFilter));

    //split recipes
    const filtered = splitRecipes(mealTemplate, matchesFilter);

    // Move recipes in mealTemplate to the top while applying the filter
    // const templateIds = new Set(mealTemplate.map(t => t.id));


    // const filtered = recipes.reduce<[Recipe[], Recipe[]]>(
    //   ([inT, notT], recipe) => {
    //     if (!matchesFilter(recipe)) return [inT, notT]; // skip early if it doesn't match
    //     return templateIds.has(recipe.id)
    //       ? [[...inT, recipe], notT]
    //       : [inT, [...notT, recipe]];
    //   },
    //   [[], []]
    // );

    return [...filtered[0], ...filtered[1]];
  }, [recipes, template, searchTerm, filter]);

  const handleAction = (actionId: number) => {
    updateTemplate(template, mealPreferences, mealsPerWeek, mealPriorities);

    switch (actionId) {
      case 1:
        generateWeeklyPlan(actionId);
        break;
      default:
        if (actionId != 2) console.warn(`Unhandled actionId: ${actionId}`);
    }
    onClose();
  };

  useEffect(() => {
    onClose();
  }, [weeklyPlan]);

  useEffect(() => {
    const fetchMealPlan = async () => {
      try {
        if (user?.id) {
          const mealTemplateRef = doc(db, 'mealTemplates', user.id);
          const mealTemplateDoc = await getDoc(mealTemplateRef);

          const fetchedMealTemplate = mealTemplateDoc.data()
          if (fetchedMealTemplate) {
            setMealPreferences(fetchedMealTemplate.mealPriority);
            fetchedMealTemplate.recipes = fetchedMealTemplate.template.map((recipe: Recipe) => ({
              ...recipe,
              id: recipe.id,
            }));
            fetchedMealTemplate.recipes = fetchedMealTemplate.recipes.filter((recipe: Recipe) => recipe.id !== undefined);
            useStore.setState({
              mealTemplate: fetchedMealTemplate.recipes,
              mealPriority: fetchedMealTemplate.mealPriority,
              mealsPerWeek: fetchedMealTemplate.mealsPerWeek,
              mealPriorities: fetchedMealTemplate.mealPriorities
            });
            setMealsPerWeek(fetchedMealTemplate.mealsPerWeek);
            setMealPriorities(fetchedMealTemplate.mealPriorities);
            if (fetchedMealTemplate) {
              onUpdateTemplate(fetchedMealTemplate.recipes);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching meal template:', error);
      }
    };
    fetchMealPlan();
  }, []);


  const updateTemplate = (updatedTemplate: Recipe[], mealPreferences: string[], mealsPerWeek: number, mealPriorities: any) => {
    // console.log("Updating: ", mealPreferences, mealsPerWeek, mealPriorities);
    updateMealTemplate(updatedTemplate, mealPreferences, mealsPerWeek, mealPriorities); // use the updated one
  }

  const clearSelection = () => {
    onUpdateTemplate([]);
    updateMealTemplate([], mealPreferences, mealsPerWeek, mealPriorities);
  }
  const toggleRecipe = (recipe: Recipe) => {
    let updatedTemplate;

    const isInTemplate = template.some(r => r.id === recipe.id);
    if (isInTemplate) {
      updatedTemplate = template.filter(r => r.id !== recipe.id);
    } else {
      updatedTemplate = [...template, recipe];
    }

    console.log("Updating template", updatedTemplate.length);
    onUpdateTemplate(updatedTemplate);
    updateMealTemplate(updatedTemplate, mealPreferences, mealsPerWeek, mealPriorities); // use the updated one
  };
  // const toggleRecipe = (recipe: Recipe) => {
  //   const isInTemplate = template.some(r => r.id === recipe.id);
  //   if (isInTemplate) {
  //     onUpdateTemplate(template.filter(r => r.id !== recipe.id));
  //   } else if (template.length < 14) {
  //     onUpdateTemplate([...template, recipe]);
  //   }
  //   updateMealTemplate(template, mealPreferences);
  //   // console.log('Updated template:', template);
  // };

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return mealPreferences.length === 3;
      case 2:
        return true;
      case 3:
        return template.length >= 4;
      default:
        return false;
    }
  };

  // console.log(filteredRecipes.length, filter);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 modal-container">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                Build Your Meal Template
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Create your personalized meal plan in just 2 simple steps
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <X size={20} />
            </button>
          </div>

          {/* Progress Steps */}
          {/* Progress Steps */}
          <div className="mt-6">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-y-6 sm:gap-y-4 lg:flex lg:items-center lg:justify-between">
              {['Set Meal Priorities', 'Choose Meal Count', 'Select Recipes'].map((step, index) => (
                <div key={step} className="flex items-center justify-start">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep > index + 1
                      ? 'bg-green-500 text-white'
                      : currentStep === index + 1
                        ? 'bg-purple text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }`}
                  >
                    {currentStep > index + 1 ? '✓' : index + 1}
                  </div>
                  <span className="ml-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                    {step}
                  </span>
                </div>
              ))}
            </div>


          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 modal-content">
          {currentStep === 1 ? (
            <div className="space-y-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  First, let's establish your meal priorities. Drag and reorder the meals based on their importance to you.
                  The system will ensure these meals are filled according to your preferences.
                </p>
              </div>

              <MealPreferences
                preferences={mealPreferences}
                onUpdatePreferences={setMealPreferences}
                mealPriorities={mealPriorities}
                onToggleChange={(val, enabled) => {
                  let obj = {
                    ...mealPriorities,
                    [val]: enabled,
                  }
                  setMealPriorities(obj);
                }}
              />
            </div>
          ) : currentStep === 2 ? (
            <div className="space-y-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  Choose how many meals you'd like to plan per week. This will determine the number of recipes needed for your template.
                  We recommend between 7-21 meals per week (roughly 1-3 meals per day).
                </p>
              </div>

              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Meals per Week
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="7"
                    max="21"
                    value={mealsPerWeek}
                    onChange={(e) => setMealsPerWeek(parseInt(e.target.value))}
                    className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-secondary"
                  />
                  <span className="text-2xl font-bold text-gray-800 dark:text-white min-w-[3ch] text-center">
                    {mealsPerWeek}
                  </span>
                </div>
                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                  <span>7 meals</span>
                  <span>21 meals</span>
                </div>

                <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <h4 className="font-medium text-gray-800 dark:text-white mb-2">
                    What this means:
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                    <li>• You'd be averaging about {Math.round(mealsPerWeek / 7)} meals a day based on your selection</li>
                    <li>• With 21 recipes, your week could include repeats or variety depending on randomization</li>
                    <li>• Adding more recipes increases the chances of unique meals</li>
                    <li>• Selecting more meals leads to more prep time — balance it with your schedule</li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Master toggle */}
              <button
                onClick={() => setAllOpen((o: boolean) => !o)}
                className="flex items-center justify-between w-full py-1.5 bg-gray-100 dark:bg-gray-800 
                   rounded-lg font-medium"
              >
                <span>{allOpen ? "Collapse All" : "Expand All"}</span>
                <ChevronDown
                  className={`transition-transform duration-300 ${allOpen ? "rotate-180" : ""}`}
                />
              </button>

              {/* Collapsible wrapper for ALL content */}
              <div
                className={`transition-all space-y-6 duration-300 overflow-hidden ${allOpen ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"
                  }`}
              >
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    Now, we recommend that you select 7-21 recipes for your weekly rotation. These will be used to generate your meal plan,
                    with priority given to your preferred meal times.
                  </p>
                </div>

                {/* Progress Bar */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Recipe Selection Progress
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {template.length} recipes selected
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-secondary transition-all duration-300"
                      style={{ width: `${Math.min((template.length / 21) * 100, 100)}%` }}
                    />
                  </div>
                </div>

                <div className="flex flex-wrap justify-between items-center gap-2">
                  <div className="relative flex-1 w-full sm:w-auto">
                    <input
                      type="text"
                      placeholder="Search recipes..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full py-2 px-4 pl-10 border border-gray-300 dark:border-gray-600 
                 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary
                 focus:border-secondary bg-white dark:bg-gray-700
                 text-gray-900 dark:text-white"
                    />
                    <Search
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      size={20}
                    />
                  </div>

                  <button
                    onClick={clearSelection}
                    className="flex items-center px-4 py-2 text-white text-sm rounded-lg 
               hover:underline hover:text-secondary/90 w-full sm:w-auto"
                  >
                    Clear Selection
                  </button>
                </div>

                <div className="relative w-full mb-6">
                  <div className="absolute inset-0 flex">
                    <span
                      className="bg-secondary-700 rounded-full transition-all duration-300"
                      style={{
                        width: "33.333%",
                        transform: `translateX(${["breakfast", "lunch", "dinner"].indexOf(filter) * 100}%)`,
                      }}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-0 border border-gray-300 rounded-full overflow-hidden">
                    {["Breakfast", "Lunch", "Dinner"].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setFilter(tab.toLowerCase())}
                        className="relative z-10 py-3 text-center text-sm"
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                </div>
              </div>



              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredRecipes.map((recipe) => {
                  const isSelected = template.some(r => r.id === recipe.id);
                  return (
                    <div
                      key={recipe.id}
                      onClick={() => toggleRecipe(recipe)}
                      className={`cursor-pointer rounded-lg border ${isSelected
                        ? 'border-secondary ring-1 ring-secondary'
                        : 'border-gray-200 dark:border-gray-700'
                        } overflow-hidden hover:border-secondary transition-colors relative`}
                    >
                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-secondary text-white p-1 rounded-full">
                          <Check size={16} />
                        </div>
                      )}
                      <img
                        src={recipe.image}
                        alt={recipe.title}
                        className="w-full h-32 object-cover"
                      />
                      <div className="p-3">
                        <h4 className="font-medium text-gray-800 dark:text-white whitespace-nowrap overflow-hidden text-ellipsis">
                          {recipe.title}
                        </h4>

                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {recipe.prepTime && !isPureNumber(recipe.prepTime) ? `${recipe.prepTime} prep · ${recipe.cookTime} cook` :
                            `${recipe.cookTime} cook`}
                        </p>
                        <div className="flex items-center mt-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${recipe.difficulty === 'Easy'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            : recipe.difficulty === 'Medium'
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            }`}>
                            {recipe.difficulty}
                          </span>
                          {Array.isArray(recipe.category) && recipe.category.map((each) => <span className="text-xs text-gray-500 dark:text-gray-400 ml-2 whitespace-nowrap overflow-hidden text-ellipsis">
                            {each}
                          </span>)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {filteredRecipes.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">No recipes found matching your search.</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-between">
          <button
            onClick={prevStep}
            className={`flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 ${currentStep === 1 ? 'invisible' : ''
              }`}
          >
            <ArrowLeft size={16} className="mr-2" />
            Previous
          </button>

          {currentStep < 3 ? (
            <button
              onClick={nextStep}
              disabled={!isStepValid()}
              className={`flex items-center px-4 py-2 bg-secondary-700 text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Next
              <ArrowRight size={16} className="ml-2" />
            </button>
          ) : (
            <button
              onClick={() => {
                setShowAlert(true)
              }}
              disabled={!isStepValid()}
              className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-500/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              All Set!
              <ArrowRight size={16} className="ml-2" />
            </button>
          )}
        </div>
      </div>

      <NewPlanAlert open={showAlert} setOpen={() => setShowAlert(!showAlert)} onClick={(actionId) => handleAction(actionId)} />
      <CreateMealModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreateMeal={(recipe) => {
          toggleRecipe(recipe);
          setShowCreateModal(false);
        }}
      />
    </div>
  );
};

export default TemplateBuilderModal;